import { useCallback, useEffect, useMemo, useState } from 'react';
import { createPortal } from 'react-dom';
import { useLocation, useNavigate } from 'react-router-dom';
import type { LucideIcon } from 'lucide-react';
import {
  BarChart3,
  Bookmark,
  ClipboardList,
  Flag,
  GitBranch,
  Headphones,
  History,
  Image,
  Info,
  KeyRound,
  LandPlot,
  Layers,
  Library,
  Lock,
  LogOut,
  Menu,
  UserX,
  MessageSquare,
  MessagesSquare,
  School,
  Shield,
  QrCode,
  Settings2,
  SlidersHorizontal,
  UserCircle,
  Users,
  Utensils,
  X,
} from 'lucide-react';

import { useAuth } from '../context/AuthContext';
import { useFactoryWorkspace } from '../context/FactoryWorkspaceContext';
import { useActiveSubject } from '../context/ActiveSubjectContext';
import { useProductStudyMode } from '../context/ProductStudyModeContext';
import { isTtqSubjectEnabled } from '../services/ttqService';
import {
  collectFactoryAttentionKeys,
  hasUnseenFactoryAttention,
  markFactoryAttentionSeen,
} from '../factory/factoryAttentionSeen';
import { factoryService } from '../services/factoryService';
import type { AuthUser } from '../types/domain';
import { UserKaleidoscopeAvatar } from './UserKaleidoscopeAvatar';
import {
  FamilyCoBuildingPlanBadge,
  userHasFamilyCoBuildingIcon,
} from './FamilyCoBuildingPlanBadge';

const GRID_ITEM_WIDTH = 80;
const GRID_ICON_SIZE = 50;

type MenuIcon = LucideIcon;

type MenuGridItem = {
  key: string;
  label: string;
  icon: MenuIcon;
  path?: string;
  href?: string;
  action?: 'signout' | 'exit-factory' | 'toggle-product-mode';
  disabled?: boolean;
  showDot?: boolean;
};

type MenuSection = {
  key: string;
  title: string;
  color: string;
  bgTint: string;
  items: MenuGridItem[];
};

/** 对外展示名：不回落到手机号 */
function getUserDisplayName(user: AuthUser): string {
  const username = user.username?.trim();
  if (username) return username;
  return '颗粒日记用户';
}

function resolveActiveMenuKey(pathname: string): string | null {
  if (pathname.startsWith('/ttq')) return 'ttq';
  if (pathname.startsWith('/learning/footprint')) return 'footprint';
  if (pathname.startsWith('/learning/position')) return 'position';
  if (pathname.startsWith('/learning/goals')) return 'goals';
  if (pathname.startsWith('/learning/subject-modules')) return 'subject-modules';
  if (pathname.startsWith('/learning/ai-chats')) return 'ai-chats';
  if (pathname.startsWith('/settings/learning') || pathname.startsWith('/review/sequence')) return 'settings';
  if (pathname.startsWith('/pas')) return 'pas';
  if (pathname.startsWith('/factory-orders')) return 'factory-orders';
  if (pathname.startsWith('/family-plan')) return 'family-plan';
  if (pathname.startsWith('/assets')) return 'assets';
  if (pathname.startsWith('/knowledge')) return 'knowledge';
  if (pathname.startsWith('/profile/models')) return 'model';
  if (pathname.startsWith('/bookmarks')) return 'bookmarks';
  if (pathname.startsWith('/annotations')) return 'annotations';
  if (pathname.startsWith('/feedback')) return 'feedback';
  if (pathname === '/profile') return 'profile';
  if (pathname.startsWith('/profile/password')) return 'password';
  if (pathname.startsWith('/profile/api-keys')) return 'api-keys';
  if (pathname.startsWith('/profile/delete')) return 'delete-account';
  if (pathname.startsWith('/download')) return 'download';
  if (pathname.startsWith('/about')) return 'about';
  if (pathname.startsWith('/privacy')) return 'privacy';
  return null;
}

function GridItem({
  item,
  color,
  bgTint,
  active,
  onPress,
}: {
  item: MenuGridItem;
  color: string;
  bgTint: string;
  active?: boolean;
  onPress: () => void;
}) {
  const Icon = item.icon;

  return (
    <button
      type="button"
      disabled={item.disabled}
      onClick={onPress}
      style={{ width: GRID_ITEM_WIDTH }}
      className={`flex flex-col items-center gap-1 rounded-xl px-1 py-2 transition ${
        item.disabled ? 'cursor-not-allowed opacity-45' : 'hover:bg-primary/5 active:bg-primary/10'
      } ${active ? 'bg-primary/10 ring-1 ring-primary/20' : ''}`}
    >
      <span
        className="relative flex items-center justify-center rounded-xl"
        style={{
          width: GRID_ICON_SIZE,
          height: GRID_ICON_SIZE,
          backgroundColor: item.disabled ? '#f0f2f5' : bgTint,
        }}
      >
        <Icon size={26} color={item.disabled ? '#8b909a' : color} />
        {item.showDot ? (
          <span
            className="absolute right-1 top-1 h-2 w-2 rounded-full border-[1.5px] border-white bg-[#E53935]"
            aria-hidden
          />
        ) : null}
      </span>
      <span
        className={`px-0.5 text-center text-[11px] leading-[15px] ${
          active ? 'font-bold text-primary' : 'font-medium text-ink'
        }`}
      >
        {item.label}
      </span>
    </button>
  );
}

export function AppMenuDrawer() {
  const { user, signOut } = useAuth();
  const { inFactory, exitFactory } = useFactoryWorkspace();
  const { activeSubject } = useActiveSubject();
  const { productMode, setProductMode } = useProductStudyMode();
  const navigate = useNavigate();
  const location = useLocation();
  const [open, setOpen] = useState(false);
  const [signOutConfirmVisible, setSignOutConfirmVisible] = useState(false);
  const [signOutPending, setSignOutPending] = useState(false);
  const [factoryExitHint, setFactoryExitHint] = useState(false);
  const [factoryAttention, setFactoryAttention] = useState(false);

  const isOffscreen = productMode === 'offscreen';
  const activeMenuItemKey = useMemo(() => resolveActiveMenuKey(location.pathname), [location.pathname]);

  useEffect(() => {
    if (!user?.id) {
      setFactoryAttention(false);
      return;
    }
    let cancelled = false;
    const poll = async () => {
      try {
        const [mine, pendingAuth, pendingRelease] = await Promise.all([
          factoryService.listMyOrdersWithHistory(),
          factoryService.listOwnerPendingBatches().catch(() => []),
          factoryService.listOwnerPendingReleaseBatches().catch(() => []),
        ]);
        if (cancelled) return;
        const keys = collectFactoryAttentionKeys({
          orders: mine.orders || [],
          pendingAuth,
          pendingRelease,
        });
        setFactoryAttention(hasUnseenFactoryAttention(user.id, keys));
      } catch {
        /* soft */
      }
    };
    void poll();
    const timer = window.setInterval(() => {
      void poll();
    }, 30_000);
    return () => {
      cancelled = true;
      window.clearInterval(timer);
    };
  }, [user?.id]);

  const markFactoryOrdersSeen = useCallback(async () => {
    if (!user?.id) return;
    try {
      const [mine, pendingAuth, pendingRelease] = await Promise.all([
        factoryService.listMyOrdersWithHistory(),
        factoryService.listOwnerPendingBatches().catch(() => []),
        factoryService.listOwnerPendingReleaseBatches().catch(() => []),
      ]);
      const keys = collectFactoryAttentionKeys({
        orders: mine.orders || [],
        pendingAuth,
        pendingRelease,
      });
      markFactoryAttentionSeen(user.id, keys);
      setFactoryAttention(false);
    } catch {
      /* soft */
    }
  }, [user?.id]);

  const closeDrawer = useCallback(() => {
    setOpen(false);
    if (!signOutPending) {
      setSignOutConfirmVisible(false);
      setFactoryExitHint(false);
    }
  }, [signOutPending]);

  useEffect(() => {
    if (!open) return;
    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';

    function onKeyDown(event: KeyboardEvent) {
      if (event.key === 'Escape') {
        closeDrawer();
      }
    }

    window.addEventListener('keydown', onKeyDown);
    return () => {
      document.body.style.overflow = previousOverflow;
      window.removeEventListener('keydown', onKeyDown);
    };
  }, [closeDrawer, open]);

  useEffect(() => {
    closeDrawer();
  }, [closeDrawer, location.pathname]);

  const handleItemPress = useCallback((item: MenuGridItem) => {
    if (item.disabled) return;
    if (item.action === 'exit-factory') {
      void exitFactory();
      closeDrawer();
      return;
    }
    if (item.action === 'toggle-product-mode') {
      if (inFactory) {
        setFactoryExitHint(true);
        return;
      }
      const next = productMode === 'offscreen' ? 'review' : 'offscreen';
      setProductMode(next);
      closeDrawer();
      navigate('/');
      return;
    }
    if (item.action === 'signout') {
      if (inFactory) {
        setFactoryExitHint(true);
        return;
      }
      setSignOutConfirmVisible(true);
      return;
    }
    if (item.href) {
      window.location.assign(item.href);
      closeDrawer();
      return;
    }
    if (item.path) {
      if (inFactory && !item.path.startsWith('/factory')) {
        setFactoryExitHint(true);
        return;
      }
      if (item.key === 'factory-orders') {
        void markFactoryOrdersSeen();
      }
      navigate(item.path);
      closeDrawer();
    }
  }, [closeDrawer, exitFactory, inFactory, markFactoryOrdersSeen, navigate, productMode, setProductMode]);

  const handleConfirmSignOut = useCallback(async () => {
    if (signOutPending) return;
    setSignOutPending(true);
    try {
      await signOut();
      setSignOutConfirmVisible(false);
      closeDrawer();
      navigate('/login', { replace: true });
    } finally {
      setSignOutPending(false);
    }
  }, [closeDrawer, navigate, signOut, signOutPending]);

  if (!user) return null;

  const sections: MenuSection[] = [
    {
      key: 'learning',
      title: '学习',
      color: '#2f6fed',
      bgTint: '#EAF2FF',
      items: [
        {
          key: 'product-mode',
          label: isOffscreen ? '切回复习模式' : '切换离屏模式',
          icon: isOffscreen ? School : Headphones,
          action: 'toggle-product-mode',
        },
        { key: 'footprint', label: '历史足迹', icon: History, path: '/learning/footprint' },
        { key: 'position', label: '学情位置', icon: GitBranch, path: '/learning/position' },
        { key: 'goals', label: '学习目标', icon: Flag, path: '/learning/goals' },
        { key: 'subject-modules', label: '学科模块', icon: Layers, path: '/learning/subject-modules' },
        { key: 'ai-chats', label: 'AI 对话', icon: MessagesSquare, path: '/learning/ai-chats' },
        { key: 'settings', label: '学习设置', icon: SlidersHorizontal, path: '/settings/learning' },
      ],
    },
    {
      key: 'resources',
      title: '资源',
      color: '#2e7d32',
      bgTint: '#E8F5E8',
      items: [
        { key: 'assets', label: '素材', icon: Image, path: '/assets' },
        { key: 'pas', label: '实战答题纸', icon: LandPlot, path: '/pas' },
        { key: 'knowledge', label: '颗粒 / 卡族', icon: Library, path: '/knowledge' },
        ...(isTtqSubjectEnabled(activeSubject) ? [{ key: 'ttq', label: '品题', icon: Utensils, path: '/ttq' }] : []),
        { key: 'model', label: '模型配置', icon: Settings2, path: '/profile/models' },
        { key: 'bookmarks', label: '收藏', icon: Bookmark, path: '/bookmarks' },
        { key: 'annotations', label: '难易标注', icon: BarChart3, path: '/annotations' },
        { key: 'feedback', label: '报错反馈', icon: MessageSquare, path: '/feedback' },
        { key: 'factory-orders', label: '颗粒工厂订单', icon: ClipboardList, path: '/factory-orders', showDot: factoryAttention },
        { key: 'family-plan', label: '家庭计划', icon: Users, path: '/family-plan' },
      ],
    },
    {
      key: 'account',
      title: '账户',
      color: '#ed6c02',
      bgTint: '#FFF3E0',
      items: [
        { key: 'profile', label: '账户详情', icon: UserCircle, path: '/profile' },
        { key: 'password', label: '修改密码', icon: Lock, path: '/profile/password' },
        { key: 'api-keys', label: 'API 密钥', icon: KeyRound, path: '/profile/api-keys' },
        { key: 'delete-account', label: '注销账号', icon: UserX, path: '/profile/delete' },
        { key: 'about', label: '关于应用', icon: Info, path: '/about' },
        { key: 'download', label: '下载 App', icon: QrCode, path: '/download?view=1' },
        { key: 'privacy', label: '隐私政策', icon: Shield, path: '/privacy' },
        { key: 'signout', label: '退出登录', icon: LogOut, action: 'signout' },
      ],
    },
  ];

  const menuTray = open ? (
    <div className="fixed inset-0 z-[100] flex items-start justify-center p-4 pt-16 sm:pt-20">
      <button
        type="button"
        aria-label="关闭菜单"
        className="absolute inset-0 bg-[rgba(8,12,18,0.42)]"
        onClick={closeDrawer}
      />

      <div
        role="dialog"
        aria-modal="true"
        aria-label="应用菜单"
        className="relative z-10 flex max-h-[min(85vh,760px)] w-full max-w-2xl flex-col overflow-hidden rounded-2xl bg-surface shadow-[0_24px_80px_rgba(0,0,0,0.22)]"
      >
        <div className="flex items-center justify-between gap-3 border-b border-border px-5 py-4">
          <div className="flex min-w-0 flex-1 items-center gap-3">
            <div className="relative shrink-0">
              <UserKaleidoscopeAvatar userId={user.id} size={52} />
              {userHasFamilyCoBuildingIcon(user) ? (
                <span className="absolute -bottom-0.5 -right-0.5">
                  <FamilyCoBuildingPlanBadge size={12} />
                </span>
              ) : null}
            </div>
            <div className="min-w-0">
              <div className="truncate text-sm font-bold text-ink">{getUserDisplayName(user)}</div>
              {userHasFamilyCoBuildingIcon(user) ? (
                <div className="mt-0.5 truncate text-[11px] font-medium text-primary">家庭共建计划</div>
              ) : null}
            </div>
          </div>
          <button
            type="button"
            onClick={closeDrawer}
            className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full border border-border bg-canvas text-ink transition hover:bg-primary/10"
            aria-label="关闭"
          >
            <X size={20} />
          </button>
        </div>

        <div className="flex-1 overflow-y-auto px-5 py-4">
          {sections.map((section) => (
            <section key={section.key} className="mb-6 last:mb-0">
              <h3 className="mb-2 px-1 text-[11px] font-bold uppercase tracking-[0.06em] text-muted">
                {section.title}
              </h3>
              <div className="flex flex-wrap gap-2">
                {section.items.map((item) => (
                  <GridItem
                    key={item.key}
                    item={item}
                    color={section.color}
                    bgTint={section.bgTint}
                    active={activeMenuItemKey === item.key}
                    onPress={() => handleItemPress(item)}
                  />
                ))}
              </div>
            </section>
          ))}
        </div>
      </div>
    </div>
  ) : null;

  const signOutDialog = signOutConfirmVisible ? (
    <div className="fixed inset-0 z-[110] flex items-center justify-center bg-black/45 p-4">
      <div className="w-full max-w-[400px] rounded-2xl bg-surface p-5 shadow-card">
        <h3 className="text-lg font-semibold text-ink">退出登录？</h3>
        <p className="mt-2 text-sm text-muted">退出后将返回欢迎页，本地登录状态会被清除。</p>
        <div className="mt-5 flex justify-end gap-2">
          <button
            type="button"
            disabled={signOutPending}
            onClick={() => setSignOutConfirmVisible(false)}
            className="rounded-lg border border-border px-4 py-2 text-sm"
          >
            取消
          </button>
          <button
            type="button"
            disabled={signOutPending}
            onClick={() => void handleConfirmSignOut()}
            className="rounded-lg bg-danger px-4 py-2 text-sm text-white disabled:opacity-50"
          >
            {signOutPending ? '退出中…' : '确认退出'}
          </button>
        </div>
      </div>
    </div>
  ) : null;

  const factoryExitDialog = factoryExitHint ? (
    <div className="fixed inset-0 z-[110] flex items-center justify-center bg-black/45 p-4">
      <div className="w-full max-w-[400px] rounded-2xl bg-surface p-5 shadow-card">
        <h3 className="text-lg font-semibold text-ink">请先退出工厂</h3>
        <p className="mt-2 text-sm text-muted">
          切换或登录其他日记账户前，需先退出工厂 Context。
        </p>
        <div className="mt-5 flex justify-end gap-2">
          <button
            type="button"
            onClick={() => setFactoryExitHint(false)}
            className="rounded-lg border border-border px-4 py-2 text-sm"
          >
            取消
          </button>
          <button
            type="button"
            onClick={() => {
              setFactoryExitHint(false);
              void exitFactory();
              closeDrawer();
            }}
            className="rounded-lg bg-primary px-4 py-2 text-sm text-white"
          >
            退出工厂
          </button>
        </div>
      </div>
    </div>
  ) : null;

  return (
    <>
      <button
        type="button"
        onClick={() => setOpen(true)}
        className="relative inline-flex h-9 w-9 shrink-0 items-center justify-center rounded-lg text-ink transition hover:bg-black/5"
        aria-label={factoryAttention ? '打开菜单，有工厂订单待处理' : '打开菜单'}
      >
        <span className="relative inline-flex">
          <Menu size={20} />
          {factoryAttention ? (
            <span
              className="absolute -right-0.5 -top-0.5 h-2 w-2 rounded-full bg-[#E53935]"
              aria-hidden
            />
          ) : null}
        </span>
      </button>

      {typeof document !== 'undefined' && menuTray ? createPortal(menuTray, document.body) : null}
      {typeof document !== 'undefined' && signOutDialog ? createPortal(signOutDialog, document.body) : null}
      {typeof document !== 'undefined' && factoryExitDialog ? createPortal(factoryExitDialog, document.body) : null}
    </>
  );
}
